import { countries } from '~constants/enums';

import { AddressItem } from './AddressItem';

export enum SupportedAddressProvider {
  GOOGLE_MAPS_GEOCODER = 'google_maps_geocoder',
  GOOGLE_MAPS_PLACES_TEXT_SEARCH = 'google_maps_places_text_search',
}

type NextBillionSuggestion = {
  place: {
    address: string;
    house: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    geopoint: { lat: number; lng: number };
  };
  dataSource: { refId: string };
};
type GoogleMapsGeocoderSuggestion = google.maps.GeocoderResult;
type GoogleMapsPlace = google.maps.places.Place;

export type AddressProviderSuggestion =
  | NextBillionSuggestion
  | GoogleMapsGeocoderSuggestion
  | GoogleMapsPlace;

interface IAddressProvider {
  decodeSuggestion: (args: AddressProviderSuggestion) => AddressItem;
}

type AddressComponentShortText = { types: string[]; shortText: string | null };

function decodeAddressComponents(components: AddressComponentShortText[]) {
  const shortTextForType = (type: string) => {
    return (
      components.find((component) => component.types.includes(type))?.shortText ?? ''
    );
  };

  const countryCode = shortTextForType('country');

  return {
    city: shortTextForType('locality') || shortTextForType('sublocality_level_1'),
    state: shortTextForType('administrative_area_level_1'),
    postalCode: shortTextForType('postal_code'),
    country: countryCode
      ? (countries.values.find((country) => country.code === countryCode) ??
        countries.default)
      : countries.default,
  };
}

export class AddressProvider {
  constructor(private readonly strategy: IAddressProvider) {}

  decodeSuggestion(args: AddressProviderSuggestion) {
    return this.strategy.decodeSuggestion(args);
  }
}

class ConcreteGoogleGeocoderAddressProvider implements IAddressProvider {
  decodeSuggestion(args: AddressProviderSuggestion) {
    const typedArgs = args as GoogleMapsGeocoderSuggestion;

    if (!typedArgs.address_components) {
      throw new Error('Invalid Google Maps API result provided.');
    }

    const { city, state, postalCode, country } = decodeAddressComponents(
      typedArgs.address_components.map((component) => ({
        types: component.types,
        shortText: component.short_name,
      })),
    );

    const address = typedArgs.formatted_address ?? '';
    const shortName = null;
    const house = '';
    const lat = typedArgs.geometry?.location?.lat() ?? 0;
    const lng = typedArgs.geometry?.location?.lng() ?? 0;
    const placeId = typedArgs.place_id ?? '';
    const fullAddress = typedArgs.formatted_address ?? '';

    return new AddressItem(
      address,
      house,
      city,
      state,
      postalCode,
      country,
      String(lat),
      String(lng),
      placeId,
      '',
      shortName,
      fullAddress,
    );
  }
}

class ConcreteGooglePlacesTextSearchAddressProvider implements IAddressProvider {
  decodeSuggestion(args: AddressProviderSuggestion) {
    const typedArgs = args as GoogleMapsPlace;

    const { city, state, postalCode, country } = decodeAddressComponents(
      (typedArgs.addressComponents ?? []).map((component) => ({
        types: component.types,
        shortText: component.shortText,
      })),
    );

    const address = typedArgs.formattedAddress ?? '';
    const shortName = typedArgs.displayName ?? null;
    const house = '';
    const lat = typedArgs.location?.lat() ?? 0;
    const lng = typedArgs.location?.lng() ?? 0;
    const placeId = typedArgs.id;
    const fullAddress = typedArgs.formattedAddress ?? '';

    return new AddressItem(
      address,
      house,
      city,
      state,
      postalCode,
      country,
      String(lat),
      String(lng),
      placeId,
      '',
      shortName,
      fullAddress,
    );
  }
}

export const concreteAddressProviderBySupportedAddressProvider = {
  [SupportedAddressProvider.GOOGLE_MAPS_GEOCODER]: ConcreteGoogleGeocoderAddressProvider,
  [SupportedAddressProvider.GOOGLE_MAPS_PLACES_TEXT_SEARCH]:
    ConcreteGooglePlacesTextSearchAddressProvider,
};
